import { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';
import { usePageNavigation } from 'hooks/pageNavigation';
import { ApiError } from 'app-types';

const playerErrorCodesToMessage: Record<number, string> = {
  1000: 'playback.error.videoStart',
  1100: 'playback.error.videoStart',
  1101: 'playback.error.location',
  1102: 'playback.error.vpn',
  1103: 'playback.error.vpn',
  1105: 'playback.error.videoStart',
  1106: 'playback.error.videoStart',
  1202: 'playback.error.videoStart',
  1203: 'playback.error.videoStart',
  2000: 'playback.error.videoStart',
  2100: 'playback.error.videoStart',
  2200: 'playback.error.videoStart',
  2300: 'playback.error.videoStart',
  4000: 'playback.error.videoStart',
  4100: 'playback.error.videoStart',
  4101: 'playback.error.videoStart',
  4200: 'playback.error.videoStart',
  4201: 'playback.error.videoStart',
  4300: 'playback.error.adBlock',
};

interface PlaybackError {
  title: string;
  message: string;
  code: number;
}

export const usePlaybackErrors = () => {
  const history = useHistory() as MemoryHistory;
  const intl = useIntl();
  const { navigateToHome } = usePageNavigation();

  const [errorCode, setErrorCode] = useState<number | null>(null);
  const onError = useCallback((code: number) => {
    setErrorCode(code);
  }, []);

  const errorId =
    playerErrorCodesToMessage[errorCode as number] || 'playback.error.generic';
  const titleId = `${errorId}.title`;
  const messageId = `${errorId}.message`;

  const error = useMemo((): PlaybackError | null => {
    const errorMessage = intl.formatMessage({
      id: messageId,
      description: 'Error Message',
    });

    const errorTitle = intl.formatMessage({
      id: titleId,
      description: 'Error Title',
      defaultMessage: intl.formatMessage({
        id: 'playback.error.fallback.title',
        description: 'Fallback Title',
      }),
    });

    const errorCodeMessage = errorCode
      ? `${intl.formatMessage({
          id: 'errors.errorCode.prefix',
        })} (${errorCode})<br><br>`
      : '';
    const messageBody = `${errorCodeMessage}${errorMessage}`;

    return errorCode
      ? {
          title: errorTitle,
          message: messageBody,
          code: errorCode,
        }
      : null;
  }, [intl, titleId, messageId, errorCode]);

  // Note: The CTA id is hardcoded for now because the Try Again
  // CTA and error message that accompanies it does not map
  // to any error messages, currently.
  const ctaId = 'playback.error.cta.okay';
  const ctaLabel = useMemo(
    () =>
      intl.formatMessage({
        id: ctaId,
        description: 'Error CTA',
      }),
    [intl],
  );
  const onCtaClick = useCallback(() => {
    if (
      errorId === 'playback.error.vpn' ||
      errorId === 'playback.error.location'
    ) {
      navigateToHome();
    } else {
      history.goBack();
    }
  }, [errorId, history, navigateToHome]);

  return {
    error,
    ctaLabel,
    onError,
    onCtaClick,
  };
};

const getErrorIdForError = (error: ApiError | null = null): string => {
  if (error && error.isConnectionError()) {
    return 'api.error.connection';
  }

  return 'api.error.generic';
};

export const useNetworkAndApiErrors = (error: ApiError | null = null) => {
  const errorId = getErrorIdForError(error);

  const titleId = `${errorId}.title`;
  const messageId = `${errorId}.message`;

  const intl = useIntl();

  const title = intl.formatMessage({
    id: titleId,
    defaultMessage: 'Oops!',
  });
  const message =
    error && error.statusCode
      ? `Error (${error.statusCode})<br><br>${intl.formatMessage({
          id: messageId,
          defaultMessage:
            'Looks like your internet was interrupted. Please try again.',
        })}`
      : intl.formatMessage({
          id: messageId,
          defaultMessage:
            'Looks like your internet was interrupted. Please try again.',
        });

  return {
    title,
    message,
  };
};

export const getHttpErrorCode = (error: unknown): number | undefined | null => {
  return error ? (error as ApiError).response?.status as number : null;
};
